import events from '../utils/events';

export const EV_FETCH_ERROR = 'EV_FETCH_ERROR';

type TMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';
type THeaders = string[][];

export type FetchRequest = {
  url: string;
  method: TMethod;
  body?: any;
  headers?: THeaders;
};

export type FetchError = {
  status?: number;
  error: unknown;
};

const defaultHeaders = new Headers([
  ['Accept', '*/*'],
  ['Content-Type', 'application/json'],
  ['Transfer-Encoding', 'application/json'],
]);

const createHeaders = (headers?: THeaders) => {
  if (!headers) {
    return defaultHeaders;
  }

  const _headers = new Headers(defaultHeaders);
  headers.forEach((h) => {
    if (_headers.has(h[0])) {
      _headers.delete(h[0]);
    }
    if (h.length >= 2) {
      _headers.append(h[0], h[1]);
    }
  });
  return _headers;
};

const jsonRequest = <TData, TBody = undefined>(
  url: string,
  method: TMethod,
  body?: TBody,
  headers?: THeaders,
  cookies?: boolean,
) =>
  new Promise<TData>((resolve, reject) => {
    fetch(url, {
      body: (body && JSON.stringify(body)) || undefined,
      method,
      headers: createHeaders(headers),
      credentials: cookies ? 'include' : 'same-origin',
    })
      .then((response) => {
        if (response.status === 204) {
          resolve(null as TData);
        } else if (response.ok) {
          return response.json().then(resolve).catch(reject);
        } else {
          return response
            .json()
            .then((error) => {
              events.emit(
                EV_FETCH_ERROR,
                { url, method, body, headers, cookies },
                { error, status: response.status },
              );
              reject(error);
            })
            .catch((error) => {
              events.emit(
                EV_FETCH_ERROR,
                { url, method, body, headers, cookies },
                { error, status: response.status },
              );
              reject(error);
            });
        }
      })
      .catch((error) => {
        events.emit(EV_FETCH_ERROR, { url, method, body, headers, cookies }, { error });
        reject(error);
      });
  });

const request = <TBody = undefined>(
  url: string,
  method: TMethod,
  body?: TBody,
  headers?: THeaders,
) =>
  fetch(url, {
    body: (body as unknown as BodyInit) || undefined,
    method,
    headers: headers ? createHeaders(headers) : undefined,
    credentials: 'same-origin',
  });

export const get = <TData>(url: string, headers?: THeaders, cookies?: boolean) =>
  jsonRequest<TData, undefined>(url, 'GET', undefined, headers, cookies);

export const post = <TData, TBody = undefined>(
  url: string,
  body?: TBody,
  headers?: THeaders,
  cookies?: boolean,
) => jsonRequest<TData, TBody>(url, 'POST', body, headers, cookies);

export const put = <TData, TBody = undefined>(
  url: string,
  body?: TBody,
  headers?: THeaders,
  cookies?: boolean,
) => jsonRequest<TData, TBody>(url, 'PUT', body, headers, cookies);

export const _delete = <TData>(url: string, headers?: THeaders, cookies?: boolean) =>
  jsonRequest<TData, undefined>(url, 'DELETE', undefined, headers, cookies);

export const postForm = <TBody = undefined>(url: string, body?: TBody, headers?: THeaders) =>
  request<TBody>(url, 'POST', body, headers || []);
